/**
 * A row of links to parts of the page. The link of the part in view is
 * marked and scrolled into the middle of the row. With `data-show-after` the
 * row stays out of sight until the element that selector names has scrolled
 * above the screen, which makes it a bar that takes over from links higher up.
 */
class SectionNav extends HTMLElement {
  #observers = [];

  connectedCallback() {
    const row = this.querySelector('[data-row]') ?? this;
    const links = new Map([...this.querySelectorAll('a[href^="#"]')].map((link) => [link.hash.slice(1), link]));
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const inView = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const now = entry.isIntersecting && links.get(entry.target.id);
          if (!now || now.classList.contains('is-current')) continue;

          for (const link of links.values()) {
            link.classList.toggle('is-current', link === now);
            link.toggleAttribute('aria-current', link === now);
          }
          if (row.scrollWidth > row.clientWidth) {
            row.scrollTo({
              left: now.offsetLeft - (row.clientWidth - now.offsetWidth) / 2,
              behavior: calm ? 'auto' : 'smooth',
            });
          }
        }
      },
      { rootMargin: '-25% 0px -65% 0px' }
    );
    for (const id of links.keys()) {
      const part = document.getElementById(id);
      if (part) inView.observe(part);
    }
    this.#observers.push(inView);

    const before = this.dataset.showAfter && document.querySelector(this.dataset.showAfter);
    if (before) {
      const passed = new IntersectionObserver(([entry]) => {
        const shown = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.classList.toggle('is-shown', shown);
        this.inert = !shown;
      });
      passed.observe(before);
      this.#observers.push(passed);
    }
  }

  disconnectedCallback() {
    for (const observer of this.#observers) observer.disconnect();
  }
}

if (!customElements.get('section-nav')) customElements.define('section-nav', SectionNav);
